import { ContextFormFieldDate } from '@bambi/component-library/src/components/forms/context/ContextFormFieldDate';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { getMetaDefaultValues } from './getMetaDefaultValues';
import type { SchemeMetaFormValues } from './metaDefaultValues';
import { milesToHundredths } from './metaDefaultValues';
import { MileageRangeSelector } from './MileageRangeSelector/MileageRangeSelector';
import { PricingWizardSpaceTypeSelection } from './PricingWizardSpaceTypeSelection';
import { ScopeCheckAlert } from './ScopeCheckAlert';
import { ServiceTypeSelector } from './ServiceTypeSelector';
import type { PricingSchemeRead } from 'src/common/external/bambi-api/bambiApi';
import { usePricingPricingSchemeConflictsCreateMutation } from 'src/common/external/bambi-api/bambiApi';
import { FormCancelButton } from 'src/common/FormCancelButton';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import { MultiSelectPayerField } from 'src/common/MultiSelectPayerContextField';
import formatServerError from 'src/common/util/serverErrorFormatter';

export type PricingWizardSchemeMetaStepProps = {
  initialValues?: SchemeMetaFormValues;
  onCancel: () => void;
  onSubmit: (values: SchemeMetaFormValues) => void;
  originalPricingSchemeValues?: PricingSchemeRead;
};

export function PricingWizardSchemeMetaStep({
  originalPricingSchemeValues,
  initialValues,
  onCancel,
  onSubmit,
}: PricingWizardSchemeMetaStepProps) {
  const methods = useForm<SchemeMetaFormValues>({
    defaultValues: getMetaDefaultValues(originalPricingSchemeValues),
  });

  const [checkForSchemeConflicts] =
    usePricingPricingSchemeConflictsCreateMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const validateDates = (startDate: string, endDate: string) => {
    const start = DateTime.fromISO(startDate);
    const end = DateTime.fromISO(endDate);

    if (start >= end) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (originalPricingSchemeValues) {
      methods.setValue(
        'payer_ids',
        originalPricingSchemeValues.payers.map((payer) => payer.payer)
      );
      return;
    }

    if (initialValues) {
      methods.reset(initialValues);
    }
  }, [methods, originalPricingSchemeValues, initialValues]);
  return (
    <div data-testid="pricing-wizard-base-config" className="mt-6">
      <FormProvider {...methods}>
        <form
          data-testid="pricing-wizard-base-config-form"
          onSubmit={methods.handleSubmit(async (values) => {
            try {
              const checkForSchemeConflictsResponse =
                await checkForSchemeConflicts({
                  priceSchemeConflict: {
                    id: originalPricingSchemeValues?.id || undefined,
                    name: values.name,
                    start_date: values.start_date,
                    end_date: values.end_date,
                    payer_ids: values.payer_ids,
                    space_types: values.space_types || ([] as string[]),
                    service_types: values.service_types || [],
                    mileage_range_hundredths_lower: milesToHundredths(
                      values.mileage_range_lower_miles
                    ),
                    mileage_range_hundredths_upper: milesToHundredths(
                      values.mileage_range_upper_miles
                    ),
                  },
                });

              if ('error' in checkForSchemeConflictsResponse) {
                return setFormError(
                  formatServerError(checkForSchemeConflictsResponse.error)
                );
              }

              if (
                'data' in checkForSchemeConflictsResponse &&
                checkForSchemeConflictsResponse.data.conflicts.length > 0
              ) {
                return setFormError(
                  `Conflicts found: ${checkForSchemeConflictsResponse.data.conflicts.join(
                    ', '
                  )}`
                );
              }
              onSubmit(values);
            } catch (e) {
              setFormError(formatServerError(e));
            }
          })}
        >
          <div className="flex max-w-xl flex-col gap-5 sm:min-w-[450px]">
            {formError && <FormErrorMessage>{formError}</FormErrorMessage>}
            <ContextFormFieldText
              fieldPath="name"
              label="Name"
              rules={{
                required: 'Name is required',
              }}
            />
            <MultiSelectPayerField
              fieldPath="payer_ids"
              rules={{
                required: 'Payers are required',
              }}
              helperText={{
                text: 'The payers the pricing scheme will apply to.',
                presentation: {
                  type: 'tooltip',
                  iconPosition: 'input-right',
                },
              }}
            />
            <PricingWizardSpaceTypeSelection />
            <div className="pr-7">
              <ServiceTypeSelector fieldPath="service_types" />
            </div>
            <div className="pr-7">
              <MileageRangeSelector
                lowerFieldPath="mileage_range_lower_miles"
                upperFieldPath="mileage_range_upper_miles"
              />
            </div>
            <div className="flex gap-2">
              <ContextFormFieldDate
                fieldPath="start_date"
                label="Start Date"
                rules={{
                  required: 'Start date is required',
                  validate: (value) => {
                    const endDate = methods.getValues('end_date');
                    if (endDate && value && !validateDates(value, endDate)) {
                      methods.setError('end_date', {
                        message: 'End date must be after start date',
                      });
                    } else if (endDate) {
                      methods.clearErrors('end_date');
                    }
                    return true;
                  },
                }}
              />
              <ContextFormFieldDate
                fieldPath="end_date"
                label="End Date"
                rules={{
                  required: 'End date is required',
                  validate: (value) => {
                    const startDate = methods.getValues('start_date');
                    if (
                      startDate &&
                      value &&
                      !validateDates(startDate, value)
                    ) {
                      return 'End date must be after start date';
                    }
                    return true;
                  },
                }}
              />
            </div>
            <ContextFormFieldTextArea
              fieldPath="description"
              label="Description"
            />
            <ScopeCheckAlert />
            <div className="mt-4 flex justify-between gap-2">
              <FormCancelButton
                onCancel={() => {
                  onCancel();
                  methods.reset();
                }}
                className="flex-grow"
              />
              <FormSubmitButton className="flex-grow" />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
